import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import { ATLAS_EMBED } from '@/lib/embedParams';

export type Locale = 'en' | 'de';

const STORAGE_KEY = 'atlas_locale';

function detectDefault(): Locale {
  if (typeof window === 'undefined') return 'en';
  // An embedding page's ?lang= wins for this page load and is never persisted
  // (docs/specs/EMBED_PARAMS.md), like ?theme=.
  if (ATLAS_EMBED.localeOverride) return ATLAS_EMBED.localeOverride;
  const stored = window.localStorage.getItem(STORAGE_KEY);
  if (stored === 'en' || stored === 'de') return stored;
  const browser = (navigator.language || '').toLowerCase();
  return browser.startsWith('de') ? 'de' : 'en';
}

interface LocaleContextValue {
  locale: Locale;
  setLocale: (l: Locale) => void;
  toggleLocale: () => void;
}

const LocaleContext = createContext<LocaleContextValue | null>(null);

export function LocaleProvider({ children }: { children: React.ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>('en');

  // Hydrate from browser/localStorage on mount (avoid SSR mismatch).
  useEffect(() => {
    setLocaleState(detectDefault());
  }, []);

  const setLocale = useCallback((l: Locale) => {
    setLocaleState(l);
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(STORAGE_KEY, l);
    }
    if (typeof document !== 'undefined') {
      document.documentElement.lang = l;
    }
  }, []);

  const toggleLocale = useCallback(() => {
    setLocale(locale === 'en' ? 'de' : 'en');
  }, [locale, setLocale]);

  // Framed: the host page switches language live with
  // { type: 'atlas-locale', locale: 'de' | 'en' }. Only from our parent, and
  // not persisted, so an embed never changes the visitor's own Atlas setting.
  useEffect(() => {
    if (typeof window === 'undefined' || window.parent === window) return;
    const onMessage = (event: MessageEvent) => {
      if (event.source !== window.parent) return;
      const data = event.data as { type?: unknown; locale?: unknown } | null;
      if (data?.type !== 'atlas-locale') return;
      if (data.locale === 'de' || data.locale === 'en') setLocaleState(data.locale);
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  // Keep <html lang> in sync.
  useEffect(() => {
    if (typeof document !== 'undefined') document.documentElement.lang = locale;
  }, [locale]);

  const value = useMemo(() => ({ locale, setLocale, toggleLocale }), [locale, setLocale, toggleLocale]);

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useLocale(): LocaleContextValue {
  const ctx = useContext(LocaleContext);
  if (!ctx) {
    // Safe fallback so components outside the provider don't crash.
    return { locale: 'en', setLocale: () => {}, toggleLocale: () => {} };
  }
  return ctx;
}
